import { ExitLoad, FolioDetail, FolioSource, WithdrawAccountDetail } from '../../types/mf-execution.types'

export const calculateTieredExitLoad = (amount: number, exitLoadSlabs: ExitLoad[]): number => {
  // Early exit if no slabs or amount is 0
  if (!exitLoadSlabs?.length || !amount) return 0

  let totalExitLoad = 0
  let remainingAmount = amount // Track how much amount is left to process

  // Sort slabs by minimum amount to process in ascending order
  // This ensures we process lower amounts first
  const sortedSlabs = [...exitLoadSlabs].sort((a, b) => a.amountRange.min - b.amountRange.min)

  for (const slab of sortedSlabs) {
    // If no amount left to process, we're done
    if (remainingAmount <= 0) break

    // Extract slab properties safely
    const { min, max } = slab?.amountRange || {}
    const { rate } = slab || {}

    // Skip invalid slabs (but allow min to be 0)
    if (min === undefined || min === null || max === undefined || max === null || rate === undefined || rate === null)
      continue

    // Handle unlimited max case (when max is -1)
    const slabMax = max === -1 ? Infinity : max

    // Check if this slab applies to our amount at all
    // The slab applies if our amount overlaps with the slab's range
    const amountStart = amount - remainingAmount
    const amountEnd = amount

    // Slab applies if there's any overlap between [amountStart, amountEnd] and [min, max]
    const slabApplies = amountEnd >= min && amountStart <= slabMax

    if (slabApplies) {
      // Calculate the actual range of this slab that applies to our amount
      const slabMin = Math.max(min, amountStart)
      const slabMaxForCalculation = Math.min(slabMax, amountEnd)

      // Calculate how much of our amount falls in this slab
      // For exit load, we want the actual amount, not the count of numbers
      const amountInThisSlab = slabMaxForCalculation - slabMin

      // Calculate exit load for this portion (rate is in percentage, so divide by 100)
      const exitLoadForThisSlab = amountInThisSlab * (rate / 100)

      // Add to total and reduce remaining amount
      totalExitLoad += exitLoadForThisSlab
      remainingAmount -= amountInThisSlab
    }
  }

  return totalExitLoad
}

export const isExternalFolio = (folio: FolioDetail | null): boolean => {
  if (!folio) return true
  return folio?.source === FolioSource.EXTERNAL || folio?.source === FolioSource.INFINITE
}

/** Same as external: no bank picker; payout goes to the bank linked on the folio. */
export const withdrawsToFolioLinkedBank = (
  folio: FolioDetail | null,
  isBankAccountsQuerySuccess: boolean,
  isBankAccountsFetchedAfterMount: boolean,
  bankAccounts: WithdrawAccountDetail[]
): boolean => {
  if (!folio) return false
  if (isExternalFolio(folio)) return true
  return isBankAccountsQuerySuccess && isBankAccountsFetchedAfterMount && bankAccounts.length === 0
}

/** Folio present; folio-linked-bank path skips account pick, PowerUp with accounts needs one. */
export const hasBasicChecksForWithdrawal = (
  selectedFolio: FolioDetail | null,
  selectedAccount: WithdrawAccountDetail | null,
  withdrawWithoutBankAccount: boolean
): boolean => {
  if (!selectedFolio) return false
  if (withdrawWithoutBankAccount) return true
  return selectedAccount != null
}

const folioSourceWithdrawalListRank = (folioSource: FolioDetail['source'] | undefined): number => {
  if (folioSource === FolioSource.POWER_UP) return 0
  if (folioSource === FolioSource.INFINITE) return 1
  if (folioSource === FolioSource.EXTERNAL) return 2
  return 3
}

export const sortFoliosForWithdrawalSelection = (folios: FolioDetail[]): FolioDetail[] => {
  return [...folios].sort((a, b) => {
    const rankA = folioSourceWithdrawalListRank(a?.source)
    const rankB = folioSourceWithdrawalListRank(b?.source)
    if (rankA !== rankB) return rankA - rankB
    return (b?.folioAmount ?? 0) - (a?.folioAmount ?? 0)
  })
}

export const isInsufficientBalanceFolio = (folio: FolioDetail | null | undefined): boolean => {
  return (folio?.folioAmount ?? 0) <= 0
}

export const sortUnavailableFoliosForWithdrawalSelection = (folios: FolioDetail[]): FolioDetail[] => {
  return [...folios].sort((a, b) => {
    const insufficientRankA = isInsufficientBalanceFolio(a) ? 0 : 1
    const insufficientRankB = isInsufficientBalanceFolio(b) ? 0 : 1
    if (insufficientRankA !== insufficientRankB) return insufficientRankA - insufficientRankB
    const dematRankA = a?.demat ? 0 : 1
    const dematRankB = b?.demat ? 0 : 1
    if (dematRankA !== dematRankB) return dematRankA - dematRankB
    return 0
  })
}
